import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import PolicySection from '../privacy-policy/components/PolicySection';
import TableOfContents from '../privacy-policy/components/TableOfContents';
import Footer from '../homepage-sales-landing/components/Footer';

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState('acceptance');

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: 'FileCheck',
      content: (
        <>
          <p className="mb-4">
            Welcome to AuthorPreneur AI, a service provided by Entrepreneur Power Network, LLC ("Company," "we," "us," or "our"), located at 813 N. Main Street, Suite 502, McAllen, TX 78501. By accessing or using our platform, you agree to be bound by these Terms of Service ("Terms").
          </p>
          <p className="mb-4">
            These Terms constitute a legally binding agreement between you and Entrepreneur Power Network, LLC. If you do not agree to these Terms, you must not access or use the AuthorPreneur AI platform.
          </p>
          <p className="mb-4">
            By creating an account, you confirm that you are at least 16 years of age and have the legal capacity to enter into this agreement. If you are accepting these Terms on behalf of an organization, you represent that you have the authority to bind that organization.
          </p>
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Your continued use of the platform after any modifications to these Terms constitutes acceptance of the updated terms.
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'services',
      title: 'Service Description',
      icon: 'Sparkles',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Platform Features</h3>
          <p className="mb-4">
            AuthorPreneur AI provides AI-powered book writing and publishing assistance, including:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>AI-assisted manuscript creation and editing using ChatGPT API</li>
            <li>Book outline generation and content structuring</li>
            <li>Publishing guidance and formatting tools</li>
            <li>Access to Authorpreneur Community for networking and support</li>
            <li>Project management and version control features</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Service Availability</h3>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>We strive to maintain 99% uptime but do not guarantee uninterrupted service</li>
            <li>Scheduled maintenance may occur with advance notice</li>
            <li>Features and functionality may be updated or modified</li>
            <li>Service availability may vary based on subscription tier</li>
          </ul>
        </>
      )
    },
    {
      id: 'account',
      title: 'Account Registration and Security',
      icon: 'UserCog',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Account Creation</h3>
          <p className="mb-4">
            To use AuthorPreneur AI, you must create an account by providing accurate and complete information:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Valid email address and contact information</li>
            <li>Secure password meeting our security requirements</li>
            <li>Payment information for subscription-based access</li>
            <li>Consent to our Privacy Policy and these Terms</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Account Responsibilities</h3>
          <p className="mb-4">You are responsible for:</p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized access</li>
            <li>Keeping your account information current and accurate</li>
          </ul>

          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                You may not share your account or allow others to access the platform using your credentials. Account sharing may result in immediate termination.
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'subscription',
      title: 'Subscription and Payment Terms',
      icon: 'CreditCard',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Subscription Tiers</h3>
          <p className="mb-4">
            AuthorPreneur AI offers multiple subscription tiers with varying features and usage limits. Current pricing and features are available on our pricing page.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Payment Processing</h3>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>All payments are processed securely through Stripe</li>
            <li>Subscriptions renew automatically unless cancelled</li>
            <li>You authorize recurring charges to your payment method</li>
            <li>Prices are subject to change with 30 days notice</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Billing and Refunds</h3>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Billing cycles begin on the date of subscription activation</li>
            <li>Refunds are available within 7 days of initial purchase</li>
            <li>Monthly subscriptions are billed monthly; annual subscriptions annually</li>
            <li>Failed payments may result in service suspension</li>
            <li>You are responsible for all applicable taxes</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Cancellation</h3>
          <p className="text-muted-foreground mb-4">
            You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period. No partial refunds are provided for mid-cycle cancellations.
          </p>
        </>
      )
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property Rights',
      icon: 'Copyright',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Your Content Ownership</h3>
          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-semibold text-foreground mb-2">You retain full ownership and copyright of:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>All manuscripts, books, and content you create</li>
                  <li>AI-generated content based on your input and direction</li>
                  <li>Outlines, notes, and project materials</li>
                </ul>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Platform Rights</h3>
          <p className="mb-4">
            Entrepreneur Power Network, LLC retains all rights to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>The AuthorPreneur AI platform, software, and technology</li>
            <li>Our brand, trademarks, and service marks</li>
            <li>Platform features, functionality, and user interface</li>
            <li>Documentation, training materials, and support content</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">License Grant</h3>
          <p className="text-muted-foreground mb-4">
            You grant us a limited, non-exclusive license to host, store, and process your content solely for the purpose of providing our services. We do not use your manuscripts to train AI models or share them with third parties without your permission.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">AI-Generated Content</h3>
          <p className="text-muted-foreground">
            Content generated through our AI tools is provided to you for your use. While you own this content, we recommend reviewing and editing AI-generated material before publication to ensure accuracy and originality.
          </p>
        </>
      )
    },
    {
      id: 'acceptable-use',
      title: 'Acceptable Use Policy',
      icon: 'ShieldCheck',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Permitted Uses</h3>
          <p className="mb-4">You may use AuthorPreneur AI for:</p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Creating original book content and manuscripts</li>
            <li>Personal or commercial writing projects</li>
            <li>Educational and professional development</li>
            <li>Collaboration within the Authorpreneur Community</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Prohibited Activities</h3>
          <p className="mb-4">You may NOT use the platform to:</p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Generate illegal, harmful, or defamatory content</li>
            <li>Infringe on third-party intellectual property rights</li>
            <li>Create spam, phishing, or malicious content</li>
            <li>Attempt to reverse engineer or exploit the platform</li>
            <li>Share account access or violate security measures</li>
            <li>Scrape, harvest, or extract data without authorization</li>
            <li>Use automated tools to access the service excessively</li>
            <li>Impersonate others or misrepresent your identity</li>
          </ul>

          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="XCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Violation of this Acceptable Use Policy may result in immediate account suspension or termination without refund.
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'community',
      title: 'Authorpreneur Community Access',
      icon: 'Users',
      content: (
        <>
          <p className="mb-4">
            Subscription tiers include access to the Authorpreneur Community, integrated through the Skool platform. Community access is subject to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Maintaining an active subscription to AuthorPreneur AI</li>
            <li>Compliance with community guidelines and conduct rules</li>
            <li>Skool platform's terms of service and policies</li>
            <li>Respectful interaction with other community members</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Community Standards</h3>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>Be respectful, constructive, and supportive</li>
            <li>Respect intellectual property and confidentiality</li>
            <li>Avoid spam, self-promotion, or off-topic content</li>
            <li>Report violations or concerns to moderators</li>
          </ul>
        </>
      )
    },
    {
      id: 'termination',title: 'Account Termination',icon: 'UserX',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Termination by You</h3>
          <p className="mb-4">
            You may terminate your account at any time by:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Canceling your subscription through account settings</li>
            <li>Contacting customer support for account deletion</li>
            <li>Following our account closure procedures</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Termination by Us</h3>
          <p className="mb-4">
            We may suspend or terminate your account if:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>You violate these Terms or our Acceptable Use Policy</li>
            <li>Payment fails or subscription expires</li>
            <li>We detect fraudulent, abusive, or illegal activity</li>
            <li>Required by law or legal process</li>
            <li>You engage in conduct harmful to other users</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Effects of Termination</h3>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Access to the platform and services will be revoked</li>
            <li>You will lose access to Authorpreneur Community</li>
            <li>Content may be retained per our data retention policy</li>
            <li>You can export your manuscripts before termination</li>
            <li>Subscription fees are non-refundable except as stated</li>
          </ul>

          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Download and backup your content regularly. We are not responsible for content loss after account termination.
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'disclaimers',title: 'Disclaimers and Warranties',icon: 'AlertTriangle',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">"As-Is" Service</h3>
          <p className="mb-4 text-muted-foreground">
            AuthorPreneur AI is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied, including but not limited to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Merchantability or fitness for a particular purpose</li>
            <li>Non-infringement of third-party rights</li>
            <li>Accuracy, reliability, or completeness of content</li>
            <li>Uninterrupted or error-free service</li>
            <li>Security or freedom from viruses or harmful code</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">AI Content Disclaimer</h3>
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <p className="text-sm text-muted-foreground">
              AI-generated content is provided as a tool to assist your writing process. We do not guarantee the accuracy, originality, or quality of AI-generated content. You are responsible for reviewing, editing, and verifying all content before publication.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Third-Party Services</h3>
          <p className="text-muted-foreground mb-4">
            We integrate with third-party services (OpenAI, Stripe, Skool) over which we have no control. We are not responsible for:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>Third-party service availability or performance</li>
            <li>Changes to third-party terms or pricing</li>
            <li>Data handling by third-party services</li>
            <li>Third-party service interruptions or errors</li>
          </ul>
        </>
      )
    },
    {
      id: 'liability',title: 'Limitation of Liability',icon: 'ShieldAlert',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Liability Cap</h3>
          <p className="mb-4 text-muted-foreground">
            To the maximum extent permitted by law, Entrepreneur Power Network, LLC's total liability for any claims arising from these Terms or your use of AuthorPreneur AI shall not exceed the amount you paid us in the 12 months preceding the claim.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Excluded Damages</h3>
          <p className="mb-4">
            We shall not be liable for:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Indirect, incidental, or consequential damages</li>
            <li>Lost profits, revenue, or business opportunities</li>
            <li>Loss of data or content (backup your work regularly)</li>
            <li>Damages arising from third-party actions or content</li>
            <li>Damages from unauthorized access to your account</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Jurisdictional Variations</h3>
          <p className="text-muted-foreground">
            Some jurisdictions do not allow limitation of liability for certain damages. In such cases, our liability shall be limited to the fullest extent permitted by applicable law.
          </p>
        </>
      )
    },
    {
      id: 'indemnification',
      title: 'Indemnification',
      icon: 'Scale',
      content: (
        <>
          <p className="mb-4">
            You agree to indemnify, defend, and hold harmless Entrepreneur Power Network, LLC, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Your use or misuse of AuthorPreneur AI</li>
            <li>Your violation of these Terms or applicable laws</li>
            <li>Your content, including copyright infringement claims</li>
            <li>Your violation of third-party rights</li>
            <li>Unauthorized access resulting from your failure to secure your account</li>
          </ul>

          <p className="text-muted-foreground">
            We reserve the right to assume exclusive defense and control of any matter subject to indemnification by you, and you agree to cooperate with our defense of such claims.
          </p>
        </>
      )
    },
    {
      id: 'dispute-resolution',title: 'Dispute Resolution and Arbitration',icon: 'Gavel',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Informal Resolution</h3>
          <p className="mb-4 text-muted-foreground">
            Before initiating any formal dispute resolution process, you agree to contact us at{' '}
            <a href="mailto:success@entrepreneurpowernetwork.com" className="text-primary hover:underline">
              success@entrepreneurpowernetwork.com
            </a>{' '}
            to attempt to resolve the matter informally. Most disputes can be resolved through good-faith communication.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Binding Arbitration</h3>
          <p className="mb-4 text-muted-foreground">
            If informal resolution fails, any dispute arising from these Terms or your use of AuthorPreneur AI shall be resolved through binding arbitration rather than in court, except:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Small claims court matters within jurisdictional limits</li>
            <li>Intellectual property disputes</li>
            <li>Injunctive or equitable relief matters</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Arbitration Rules</h3>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Arbitration shall be conducted by the American Arbitration Association (AAA)</li>
            <li>Proceedings shall be governed by AAA Commercial Arbitration Rules</li>
            <li>Arbitration location: McAllen, Texas or mutually agreed location</li>
            <li>Each party bears its own costs and attorneys' fees unless awarded by arbitrator</li>
            <li>Arbitrator's decision is final and binding</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Class Action Waiver</h3>
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">
              You agree that disputes will be resolved on an individual basis only. You waive the right to participate in class actions, class arbitrations, or representative proceedings.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'governing-law',title: 'Governing Law and Jurisdiction',icon: 'MapPin',
      content: (
        <>
          <p className="mb-4">
            These Terms shall be governed by and construed in accordance with the laws of the State of Texas, United States, without regard to its conflict of law provisions.
          </p>
          <p className="mb-4 text-muted-foreground">
            For any disputes not subject to arbitration, you agree to submit to the exclusive jurisdiction of the state and federal courts located in Hidalgo County, Texas.
          </p>
          <p className="text-muted-foreground">
            If you are accessing the platform from outside the United States, you are responsible for compliance with local laws and regulations in your jurisdiction.
          </p>
        </>
      )
    },
    {
      id: 'modifications',title: 'Modifications to Terms',icon: 'Edit',
      content: (
        <>
          <p className="mb-4">
            We reserve the right to modify these Terms at any time. When we make changes:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>We will update the "Last Updated" date at the top of this document</li>
            <li>We will notify you via email or platform notification for material changes</li>
            <li>Changes become effective 30 days after notification for existing users</li>
            <li>Continued use after changes constitutes acceptance</li>
          </ul>

          <p className="mb-4 text-muted-foreground">
            If you do not agree to modified Terms, you must discontinue use and cancel your subscription before the changes take effect.
          </p>

          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                We recommend reviewing these Terms periodically to stay informed of any updates.
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'general',title: 'General Provisions',icon: 'FileText',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Entire Agreement</h3>
          <p className="mb-4 text-muted-foreground">
            These Terms, together with our Privacy Policy and any other policies referenced herein, constitute the entire agreement between you and Entrepreneur Power Network, LLC regarding your use of AuthorPreneur AI.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Severability</h3>
          <p className="mb-4 text-muted-foreground">
            If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Waiver</h3>
          <p className="mb-4 text-muted-foreground">
            No waiver of any term or condition shall be deemed a further or continuing waiver of such term or any other term. Our failure to assert any right under these Terms does not constitute a waiver of that right.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Assignment</h3>
          <p className="mb-4 text-muted-foreground">
            You may not assign or transfer these Terms or your account without our prior written consent. We may assign these Terms in connection with a merger, acquisition, or sale of assets.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Force Majeure</h3>
          <p className="text-muted-foreground">
            We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including acts of God, natural disasters, war, terrorism, labor disputes, or internet service interruptions.
          </p>
        </>
      )
    },
    {
      id: 'contact',title: 'Contact Information',icon: 'Mail',
      content: (
        <>
          <p className="mb-6">
            If you have questions, concerns, or disputes regarding these Terms of Service, please contact us:
          </p>
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <div>
              <h4 className="font-semibold text-foreground mb-2">Company Name</h4>
              <p className="text-muted-foreground">Entrepreneur Power Network, LLC</p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-2">Email Support</h4>
              <a href="mailto:success@entrepreneurpowernetwork.com" className="text-primary hover:underline">
                success@entrepreneurpowernetwork.com
              </a>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-2">Physical Address</h4>
              <p className="text-muted-foreground">
                813 N. Main Street, Suite 502<br />
                McAllen, TX 78501<br />
                United States
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-2">Response Time</h4>
              <p className="text-muted-foreground">
                We strive to respond to all inquiries within 2 business days.
              </p>
            </div>
          </div>
        </>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Terms of Service - AuthorPreneur AI</title>
        <meta name="description" content="Read the Terms of Service for AuthorPreneur AI platform provided by Entrepreneur Power Network, LLC" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="BookOpen" size={24} color="var(--color-primary)" />
                </div>
                <span className="text-xl font-heading font-semibold text-foreground">
                  AuthorPreneur AI
                </span>
              </Link>
              <Link 
                to="/legal-pages-hub"
                className="text-sm text-muted-foreground hover:text-primary flex items-center"
              >
                <Icon name="ArrowLeft" size={16} className="mr-1" />
                Back to Legal Hub
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            {/* Page Header */}
            <div className="mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-6">
                <Icon name="FileText" size={32} color="var(--color-primary)" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                Terms of Service
              </h1>
              <p className="text-lg text-muted-foreground mb-2">
                Last Updated: December 8, 2025
              </p>
              <p className="text-muted-foreground">
                Effective Date: December 8, 2025
              </p>
            </div>

            <div className="grid lg:grid-cols-4 gap-8">
              {/* Table of Contents - Sidebar */}
              <div className="lg:col-span-1">
                <TableOfContents 
                  sections={sections} 
                  activeSection={activeSection}
                  onSectionClick={setActiveSection}
                />
              </div>

              {/* Terms Content */}
              <div className="lg:col-span-3 space-y-8">
                {sections?.map((section) => (
                  <PolicySection
                    key={section?.id}
                    section={section}
                    isActive={activeSection === section?.id}
                  />
                ))}

                {/* Download Option */}
                <div className="bg-muted/50 border border-border rounded-xl p-6 mt-12">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Download PDF Version
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Save a copy of our terms of service for offline reference
                      </p>
                    </div>
                    <button className="flex items-center space-x-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors">
                      <Icon name="Download" size={18} />
                      <span className="text-sm font-medium">Download</span>
                    </button>
                  </div>
                </div>

                {/* Acknowledgment */}
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 mt-8">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={24} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Acknowledgment of Terms
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        By using AuthorPreneur AI, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you have any questions, please contact us before using the platform.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TermsOfService;